import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Image,
  TouchableOpacity,
  Alert,
  ScrollView,
  ActivityIndicator,
  Modal,
  Pressable,
  Platform,
  Animated,
} from 'react-native';
import {
  v2GetAccountCloseOptions,
  v2CreateAccountClose,
  v2GetMyBalances,
} from '../../../services/v2/api';
import type {
  V2Balance,
  V2AccountActionOptions,
} from '../../../types/v2';
import TabBar from '../../../components/TabBar';
import SimpleHeader from '../../../components/SimpleHeader';
import TokenIcon from '../../../components/UIComponents/TokenIcon';
import { COLORS } from '../../../theme/themeTokens';
import { COLORS_WRONG } from '../../../theme/colorsWrong';
import PINConfirmModal from '../../../components/UIComponents/PINConfirmModal';
import { usePIN } from '../../../contexts/PINContext';
import { formatDepositMeta } from '../../../utils/depositMeta';
import { formatTokenAmount } from '../../../utils/amountFormat';
import { useSwipeDownDismiss } from '../../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP, WEB_SHEET_MODAL_ANIMATION } from '../../../../web/sheet';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';

// Picker top inset — same formula TopUp / Outflow use so the bottom
// sheet's top edge sits ABOVE the "From deposit account" label,
// covering it instead of starting flush with the tile. 109dp is the
// stack of (header + tile-label + tile-row + margins) measured on the
// reference iPhone 13 mock; status-bar inset is the per-platform
// status-bar height layered on top.
const PICKER_TOP_INSET = (Platform.OS === 'android' ? 28 : 20) + 109;

// formatBalanceLikeCard — теперь delegate к formatTokenAmount
// чтобы UI-балансы шли через ту же string-truncation что и Maximum-кнопка.
// Раньше функция использовала toFixed (округление вверх) — это давало
// рассинхрон: balance label показывал "3,06839508" а Maximum давал
// "3,06839507" для того же source-значения. Теперь оба пути truncate.
// Потолок знаков на экране живёт в formatTokenAmount: token_decimals — это
// точность контракта (18 у USDT), а не то, сколько цифр читает человек.
const formatBalanceLikeCard = (amount: number | string, decimals = 2): string =>
  formatTokenAmount(amount, decimals);

/** "DD.MM.YY" — compact term-range form. */
const formatTermDate = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
};

interface CloseDepositScreenProps {
  accountId: string;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onCloseSuccess?: () => void;
}

const CloseDepositScreen: React.FC<CloseDepositScreenProps> = ({
  accountId,
  onBack,
  onNavigate,
  onCloseSuccess,
}) => {
  const [options, setOptions] = useState<V2AccountActionOptions | null>(null);
  const [loading, setLoading] = useState(true);
  // Error state — set when the backend can't resolve the original
  // source (deleted balance / already-closed / legacy account without
  // an open-deposit row). When non-null the screen renders an error
  // tile and disables the CTA.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  // Success modal — поведение симметрично OpenDepositScreen: после
  // подтверждения PIN'ом + успешного API-вызова показываем "под рассмотрением"
  // notice. Закрытие → onCloseSuccess + onBack.
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per closing: a press after a lost answer resends it with the
  // same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<{ accountId: string }>();

  // Active accountId — defaults to the prop but switched when the user
  // picks a different open deposit via the "From deposit account" picker.
  // Driving the options-fetch effect off this state (instead of the
  // prop) lets the user swap between their open deposits without leaving
  // the screen.
  const [activeAccountId, setActiveAccountId] = useState<string>(accountId);
  // All user's investment balances — populated once on mount; the
  // picker bottom-sheet renders from here. Includes legacy investment
  // accounts that pre-date v1_2_deposits entirely (no deposit-app row,
  // `deposit_id` null on the balance). Selecting a legacy row triggers
  // the backend's `original_source_unavailable` 400 → renders the
  // existing error tile.
  const [investmentBalances, setInvestmentBalances] = useState<V2Balance[]>([]);
  const [pickerVisible, setPickerVisible] = useState(false);
  // Swipe-to-dismiss for the "From deposit account" picker.
  const pickerSwipe = useSwipeDownDismiss(
    pickerVisible,
    () => setPickerVisible(false),
  );
  // Picker positioning switched from per-tap measureInWindow to the
  // fixed PICKER_TOP_INSET constant — matches TopUp / Outflow so the
  // sheet sits above the tile-label rather than aligned to the tile.
  const openPicker = () => {
    // Skip the picker when there's only one deposit — the chevron
    // is hidden in that case too, so the tile reads as non-interactive.
    if (investmentBalances.length < 2) {
      return;
    }
    setPickerVisible(true);
  };

  // One-shot fetch of the user's investment balances used as the
  // picker's data source. Same backend feed as the dashboard
  // ("Deposits" tile) — so any investment row visible there is
  // selectable here. Independent from the per-account options
  // fetch below — the list shouldn't reload when the user picks a
  // different deposit, only the selected deposit's close-options does.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await v2GetMyBalances();
        if (cancelled) {
          return;
        }
        // Filter to classic deposit accounts. Note: после rename'а
        // (migrations 00096/00098) account_type_code='deposit' = классический
        // срочный депозит, а 'investment' = T-Fund-like (бывший t_fund) —
        // не для этого screen'а.
        const invest = (res?.balances ?? []).filter(
          (b: V2Balance) => b.account_type_code === 'deposit',
        );
        setInvestmentBalances(invest);
      } catch (err) {
        console.warn('[CloseDeposit] Failed to load investment balances:', err);
        setInvestmentBalances([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        // Single fetch — close-options carries everything the screen
        // needs: source / destination / settlement preview (incl. the
        // formula-derived penalty_interest used by the "Withheld
        // interest" row).
        const opts = await v2GetAccountCloseOptions(activeAccountId);
        if (cancelled) {
          return;
        }
        setOptions(opts);
        // Backend signals legacy / OneAdmin-created accounts via
        // close_disabled_reason + empty balances (always 200 OK so
        // the deposit summary still renders). Translate the reason
        // into the same loadError tile that used to be driven by 4xx.
        if (opts?.close_disabled_reason) {
          setLoadError(
            opts.close_disabled_message
              || 'Не удалось определить исходный счёт. Закрытие невозможно — обратитесь в поддержку.',
          );
        }
      } catch (err) {
        // Genuine network / 5xx failure — keep the existing fallback.
        const msg = err instanceof Error
          ? err.message
          : 'Не удалось определить исходный счёт. Закрытие невозможно — обратитесь в поддержку.';
        console.warn('[CloseDeposit] Failed to load options:', err);
        setOptions(null);
        setLoadError(msg);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeAccountId]);

  // Active investment-balance the screen currently renders — the same
  // row the picker is "checked" against. Single source for tile copy
  // and numeric breakdown — works for both modern (open-row exists)
  // and legacy / OneAdmin-created accounts.
  const activeBalance: V2Balance | null = useMemo(
    () => investmentBalances.find(b => b.account_id === activeAccountId) ?? null,
    [investmentBalances, activeAccountId],
  );

  // Destination — only present on modern accounts (legacy returns
  // close_disabled_reason and an empty balances array).
  const destinationBalance: V2Balance | null = options?.balances?.[0] ?? null;

  const currency = activeBalance?.token_symbol || 'USDT';

  // "USDT · 3 months · 3%" — unified deposit caption via
  // formatDepositMeta. Falls back to symbol-only when term/apy are
  // NULL (legacy contract-less rows).
  const sourceLabel = useMemo(() => {
    if (!activeBalance) return '';
    const symbol = activeBalance.token_symbol || 'USDT';
    const apyFraction = activeBalance.apy ? parseFloat(activeBalance.apy) : null;
    return formatDepositMeta(symbol, activeBalance.term, apyFraction) || symbol;
  }, [activeBalance]);

  const sourceDateRange = useMemo(() => {
    if (!activeBalance) return '';
    const from = formatTermDate(activeBalance.deposit_date_from);
    const to = formatTermDate(activeBalance.deposit_date_to);
    return from && to ? `${from} – ${to}` : '';
  }, [activeBalance]);

  // 2-row info card: "Principal returned" + "Accrued interest".
  // principalReturned = full investment balance (= what's locked and
  // will be consumed on confirm). accruedInterest = APY-based income
  // that backend returns as `accrued_interest` (I = P × APY × d / 365,
  // capped at term). User receives principalReturned + accruedInterest
  // on admin confirm. Sourced from the same close-options response
  // so no extra fetch is needed. Field was `penalty_interest` prior
  // to backend rewrite — см. INVESTMENT_DEPOSITS.md §5.
  const principalReturned = activeBalance ? parseFloat(activeBalance.amount ?? '0') || 0 : 0;
  const accruedInterest = options?.accrued_interest
    ? parseFloat(options.accrued_interest) || 0
    : 0;
  // Те же три суммы, но для показа — строками как их прислал бэкенд.
  // Складывать principal и interest во float'ах и печатать результат — это
  // и дало "+ 240 090,0821917808 2 USDT" в success-модалке. Числа выше
  // остаются: они нужны только для сравнений (accruedInterest > 0).
  // Fallback'и — на случай старого бэкенда без этих полей.
  const principalText = options?.principal_amount ?? activeBalance?.amount ?? '0';
  const accruedText = options?.accrued_interest ?? '0';
  const payoutText =
    options?.payout_amount ?? String(principalReturned + accruedInterest);

  // Early closure = today is before the active deposit's maturity date.
  const isEarlyClosure = useMemo(() => {
    if (!activeBalance?.deposit_date_to) return false;
    const to = new Date(activeBalance.deposit_date_to);
    if (Number.isNaN(to.getTime())) return false;
    return Date.now() < to.getTime();
  }, [activeBalance]);

  // Same-day close — запрещено. Backend всё равно отрегает (penalty
  // settlement opens at d≥1) но проще не давать юзеру нажимать.
  // Сравниваем по локальной (year, month, day) триаде чтобы любая
  // тайм-зона была OK.
  const isOpenedToday = useMemo(() => {
    if (!activeBalance?.deposit_date_from) return false;
    const from = new Date(activeBalance.deposit_date_from);
    if (Number.isNaN(from.getTime())) return false;
    const now = new Date();
    return from.getFullYear() === now.getFullYear()
      && from.getMonth() === now.getMonth()
      && from.getDate() === now.getDate();
  }, [activeBalance]);

  // Show the early-closure warning bar whenever the deposit is being
  // closed BEFORE its maturity date, even when accrued_interest is 0
  // (current backend always returns 0 — placeholder). The bar communicates
  // the policy intent: "closing early forfeits any interest that would
  // have accrued by maturity", which holds regardless of the numeric value.
  const showEarlyWarning = isEarlyClosure;

  // Pending close application — backend сигнализирует через options.
  // has_pending_close. Если есть — disable submit + добавим warning line
  // первой в banner.
  const hasPendingClose = options?.has_pending_close === true;

  // Error state from initial load → CTA disabled, error tile shown.
  const hasError = !loading && (!!loadError || !destinationBalance);
  const canSubmit = !loading && !hasError && !isSubmitting && !isOpenedToday && !hasPendingClose;

  const handleConfirm = () => {
    if (!canSubmit) {
      return;
    }
    if (!isPINSetup || isPINSkipped) {
      executeConfirm();
    } else {
      setPinModalVisible(true);
    }
  };

  const executeConfirm = async () => {
    setPinModalVisible(false);
    setIsSubmitting(true);
    try {
      // Используем activeAccountId (тот что юзер выбрал в picker'е),
      // НЕ accountId (с какого депозита зашли). Юзер мог переключиться
      // на другой депозит через picker — bug был в том что заявка
      // отправлялась на исходный депозит, а не на выбранный.
      // Body is empty by design — backend resolves destination from
      // the open-deposit row server-side.
      const attempt = idempotency.attempt({ accountId: activeAccountId });
      await v2CreateAccountClose(attempt.request.accountId, {} as never, attempt.key);
      idempotency.answered();
      setSuccessModalVisible(true);
    } catch (error) {
      idempotency.failed(error);
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      Alert.alert('Close failed', errorMessage, [{ text: 'OK' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessModalVisible(false);
    onCloseSuccess?.();
    onBack();
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      <SimpleHeader title="Close deposit" onBack={onBack} backgroundVariant="primaryNavy" />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* From deposit account — tappable picker; opens the bottom-sheet
            below to switch between the user's open-deposit applications.
            Picker rendered when there's 2+ open deposits; with a single
            row tile stays static (no point in a 1-item sheet). */}
        <Text style={styles.outerCardLabel}>From deposit account</Text>
        {loading ? (
          <View style={[styles.networkSelector, styles.networkSelectorCenter]}>
            <ActivityIndicator size="small" color={COLORS.brandOrange} />
          </View>
        ) : (
          <TouchableOpacity
            style={styles.networkSelector}
            activeOpacity={investmentBalances.length > 1 ? 0.7 : 1}
            disabled={investmentBalances.length < 2}
            onPress={openPicker}
          >
            <TokenIcon
              symbol={currency}
              iconUrl={options?.account.icon_url ?? null}
              style={styles.networkSelectorIcon}/>
            <View style={styles.networkTextContainer}>
              <Text style={styles.networkName}>{sourceLabel}</Text>
              {sourceDateRange ? (
                <Text style={styles.networkSubtitle}>{sourceDateRange}</Text>
              ) : null}
            </View>
            {/* Hide the picker chevron when there's nothing to switch
                between — matches TopUp / Outflow. The TouchableOpacity
                is already disabled in that case. */}
            {investmentBalances.length > 1 ? (
              <Image
                source={require('../../../assets/arrow_left_20.png')}
                style={styles.chevronIcon}
                resizeMode="contain"
              />
            ) : null}
          </TouchableOpacity>
        )}

        {/* Destination picker removed: backend resolves the original
            source of the deposit and uses it as the only destination.
            User doesn't pick. If resolution fails (deleted source /
            already closed) we render the error tile below instead of
            the picker. */}

        {/* Principal + accrued/planned interest info card. Interest всегда
            положительный, добавляется к principal. Label меняется по
            options.is_planned (backend'ская ветка): planned = "Interest
            earned" (полный term'овский APY), early = "Accrued interest"
            (маленький bonus по close_penalty_rate). */}
        {!loading && (
          <View style={styles.infoCard}>
            <View style={styles.infoCardRow}>
              <Text style={styles.amountLabel}>Principal returned</Text>
              <Text style={styles.amountValue}>
                {formatBalanceLikeCard(principalText, options?.balance?.token_decimals)} {currency}
              </Text>
            </View>
            <View style={styles.infoCardDivider} />
            <View style={styles.infoCardRow}>
              <Text style={styles.amountLabel}>
                {options?.is_planned ? 'Interest earned' : 'Accrued interest'}
              </Text>
              <Text
                style={[
                  styles.amountValue,
                  accruedInterest > 0 ? styles.amountValuePositive : null,
                ]}
              >
                {accruedInterest > 0 ? '+' : ''}
                {formatBalanceLikeCard(accruedText, options?.balance?.token_decimals)} {currency}
              </Text>
            </View>
          </View>
        )}

        {/* Early-closure warning — soft-red bar с иконкой. Может быть
            одна или две строки:
              - isOpenedToday: "Available from the next day after opening."
                (показывается ТОЛЬКО в день открытия — кнопка disabled)
              - isEarlyClosure: "Early closure withheld all accrued interest"
                (любой день до maturity). */}
        {(hasPendingClose || showEarlyWarning || isOpenedToday) && (
          <View style={styles.warningBar}>
            <Image
              source={require('../../../assets/warning.png')}
              style={styles.warningIcon}
              resizeMode="contain"
            />
            <View style={styles.warningTextContainer}>
              {hasPendingClose && (
                <Text style={styles.warningTextLine}>
                  Your close application is being processed
                </Text>
              )}
              {isOpenedToday && (
                <Text style={styles.warningTextLine}>
                  Available from the next day after opening.
                </Text>
              )}
              {showEarlyWarning && (
                <Text style={styles.warningTextLine}>
                  Early closure withheld all accrued interest
                </Text>
              )}
            </View>
          </View>
        )}

        {/* Error tile — backend couldn't resolve the original source
            (already closed / legacy without open-deposit / source
            deleted). CTA is disabled below via canSubmit. */}
        {hasError && (
          <View style={styles.warningBar}>
            <Image
              source={require('../../../assets/warning.png')}
              style={styles.warningIcon}
              resizeMode="contain"
            />
            <Text style={styles.warningText}>
              {loadError || 'Не удалось определить исходный счёт. Закрытие невозможно — обратитесь в поддержку.'}
            </Text>
          </View>
        )}

        {/* Close deposit — destructive style (brandOrange). */}
        <TouchableOpacity
          style={[
            styles.closeButton,
            (!canSubmit || isSubmitting) && styles.closeButtonDisabled,
          ]}
          onPress={handleConfirm}
          activeOpacity={canSubmit && !isSubmitting ? 0.85 : 1}
          disabled={!canSubmit || isSubmitting}
        >
          <Text style={styles.closeButtonText}>
            {isSubmitting ? 'Processing...' : 'Close deposit'}
          </Text>
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />

      <PINConfirmModal
        visible={pinModalVisible}
        title="Confirm close"
        subtitle={`Close ${currency} deposit`}
        onSuccess={executeConfirm}
        onCancel={() => setPinModalVisible(false)}
      />

      {/* "Application is under consideration" modal — после успешного
          close-API call'а. Симметричен OpenDepositScreen success modal'у.
          Закрытие (X / tap outside / OK) → onCloseSuccess + onBack. */}
      <Modal
        visible={successModalVisible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={handleSuccessClose}
      >
        <Pressable style={styles.successOverlay} onPress={handleSuccessClose}>
          <Pressable
            style={styles.successCard}
            onPress={(e) => e.stopPropagation()}
          >
            <TouchableOpacity
              style={styles.successCloseBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              onPress={handleSuccessClose}
              activeOpacity={0.6}
            >
              <Text style={styles.successCloseGlyph}>×</Text>
            </TouchableOpacity>

            <Image
              source={require('../../../assets/percentage.png')}
              style={styles.successIcon}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>Request sent</Text>
            <Text style={styles.successSubtitle}>
              Operation is being processed.
            </Text>
            {/* Amount row — Close это возврат на current → знак плюс.
                Сумма = principal + accrued interest (то что фактически
                получит юзер на current после admin confirm; совпадает
                с backend'ским payout_amount = P + I). */}
            <View style={styles.successAmountRow}>
              <Text style={styles.successAmount}>
                {`+ ${formatBalanceLikeCard(
                  payoutText,
                  options?.balance?.token_decimals,
                )} `}
              </Text>
              <Text style={styles.successCurrency}>{currency}</Text>
            </View>

            <TouchableOpacity
              style={styles.successOkButton}
              activeOpacity={0.75}
              onPress={handleSuccessClose}
            >
              <Text style={styles.successOkButtonText}>Ok</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Open-deposits picker bottom-sheet — opens from the
          "From deposit account" tile when the user has 2+ open
          deposits. Each row represents one kind='open' application;
          legacy rows (no investment_account_id) are rendered with a
          status pill and, on tap, switch the screen into the
          error-tile state via activeAccountId="". */}
      <Modal
        visible={pickerVisible}
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'fade'}
        transparent
        statusBarTranslucent
        onRequestClose={() => setPickerVisible(false)}
      >
        <View style={[styles.modalOverlay, WEB_SHEET_BACKDROP]}>
          {/* The backdrop is a sibling of the sheet, not its parent: a
              Pressable above the list takes the touch on Android and the
              native scroll never sees it. */}
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setPickerVisible(false)} />
          <Animated.View
            style={[
              styles.modalContent,
              { marginTop: PICKER_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: pickerSwipe.translateY }] },
            ]}
            {...pickerSwipe.panHandlers}
          >
            <View style={styles.modalHandle} />
            <Text style={styles.modalTitle}>Choose deposit</Text>
            <ScrollView showsVerticalScrollIndicator={false} {...pickerSwipe.listProps}>
              {investmentBalances.map((bal) => {
                const balAccountId = bal.account_id ?? '';
                const isActive = !!balAccountId && balAccountId === activeAccountId;
                const symbol = bal.token_symbol || 'USDT';
                const fromStr = formatTermDate(bal.deposit_date_from);
                const toStr = formatTermDate(bal.deposit_date_to);
                const dateRange = fromStr && toStr ? `${fromStr} – ${toStr}` : fromStr || toStr;
                // APY = bal.apy (fraction "0.030000") × 100. After
                // backend migration 00087 the field is populated from
                // v1_2_accounts.apy for both modern and legacy accounts.
                // Title now uses the unified deposit caption —
                // "USDT · 3 months · 3%" — see formatDepositMeta.
                const apyFraction = bal.apy ? parseFloat(bal.apy) : null;
                const titleParts = formatDepositMeta(symbol, bal.term, apyFraction);
                const subtitleParts = [
                  dateRange,
                  `${formatBalanceLikeCard(bal.amount, bal.token_decimals)} ${symbol}`,
                ].filter(Boolean);
                return (
                  <TouchableOpacity
                    key={bal.id}
                    style={[styles.modalOption, isActive && styles.modalOptionSelected]}
                    activeOpacity={0.7}
                    onPress={() => {
                      setPickerVisible(false);
                      setActiveAccountId(balAccountId);
                    }}
                  >
                    <TokenIcon
                      symbol={symbol}
                      iconUrl={bal.icon_url ?? null}
                      style={styles.modalOptionIcon}/>
                    <View style={styles.modalOptionTextContainer}>
                      <Text style={styles.modalOptionName}>{titleParts}</Text>
                      <Text style={styles.modalOptionSubtitle}>
                        {subtitleParts.join(' · ')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
              </ScrollView>
          </Animated.View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  contentContainer: {
    // 20 dp — uniform with TopUp / Outflow so the first label sits
    // at the same distance from the header on every deposit screen.
    paddingTop: 20,
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  outerCardLabel: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 17,
    color: COLORS.blueSteel,
    marginBottom: 8,
  },
  networkSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 60,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 16,
  },
  networkSelectorCenter: {
    justifyContent: 'center',
    minHeight: 64,
  },
  networkSelectorIcon: {
    width: 28,
    height: 28,
    marginRight: 12,
  },
  networkTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  networkName: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  networkSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  networkBalance: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  chevronIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },
  infoCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginBottom: 16,
  },
  infoCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
  },
  infoCardDivider: {
    height: 1,
    backgroundColor: COLORS.borderMuted,
  },
  amountLabel: {
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  amountValue: {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
    textAlign: 'right',
  },
  amountValueAlert: {
    color: COLORS_WRONG.amountInvalid,
  },
  amountValuePositive: {
    // Mint для accrued interest (доход добавляется к principal). Тот же
    // цвет что и splitValueAccrued на DepositAccountScreen / Investment
    // AccountScreen для консистентности.
    color: '#5EC07E',
  },
  /* Soft-red bar matching the prototype — leading warning icon + one
     line of red text on a tinted background. */
  warningBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: COLORS_WRONG.errorBg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS_WRONG.alertRed,
    marginBottom: 16,
  },
  warningIcon: {
    width: 20,
    height: 20,
    marginRight: 10,
    tintColor: COLORS_WRONG.alertRed,
  },
  warningText: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: COLORS_WRONG.alertRed,
  },
  // Контейнер для нескольких строк текста рядом с warning-иконкой.
  // flex:1 живёт здесь чтобы container растянулся по ширине; warningBar
  // alignItems:'center' выравнивает иконку по вертикали относительно
  // всего блока двух строк.
  warningTextContainer: {
    flex: 1,
  },
  // Одна строка multi-line warning'а: те же визуальные параметры что у
  // warningText, но без flex:1 — иначе в column-контейнере строки
  // схлопываются по высоте и буквы обрезаются.
  warningTextLine: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: COLORS_WRONG.alertRed,
  },
  noDestinationHint: {
    marginTop: 4,
    marginBottom: 16,
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: COLORS.blueSteel,
    textAlign: 'center',
  },
  /* Destructive CTA — brandOrange filled, white label. Larger height
     than navy CTAs in other screens to match the prototype's emphasis. */
  closeButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    height: 52,
    backgroundColor: COLORS.brandOrange,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  closeButtonDisabled: {
    opacity: 0.45,
  },
  closeButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  // Absolutely positioned, not flex: 1 — on Android the Modal's own
  // container does not hand a definite height down, so a flexed overlay
  // sizes itself to its content: the sheet's ScrollView grew as tall as
  // the list, swallowed the sheet's bottom padding, ran off the screen
  // and had nothing left to scroll. Pinning all four edges gives the
  // chain a real height and the list scrolls again.
  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
  },
  modalContent: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
  },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.textPrimary,
    marginBottom: 16,
  },
  modalTitleAside: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 8,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: {
    borderColor: COLORS.brandOrange,
  },
  modalOptionIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  modalOptionTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  modalOptionName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  modalOptionSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  modalOptionAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  modalOptionCheck: {
    width: 20,
    height: 20,
    marginLeft: 8,
    tintColor: COLORS.brandOrange,
  },

  // ── "Application is under consideration" success modal ───────────────
  // Скопировано из OpenDepositScreen для визуальной consistency двух
  // deposit-flow'ов. Если потом захотим extract'ить — кандидат на
  // SharedSuccessModal component.
  successOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  successCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingTop: 48,
    paddingBottom: 24,
    alignItems: 'center',
  },
  successCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successCloseGlyph: {
    fontSize: 26,
    lineHeight: 28,
    fontWeight: '400',
    color: COLORS.steelNavy,
  },
  successIcon: {
    width: 64,
    height: 64,
    marginBottom: 28,
  },
  successTitle: {
    fontFamily: 'Inter',
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 32,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  successSubtitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
    textAlign: 'center',
    marginTop: 6,
  },
  // Amount row mirror'ит OpenDepositScreen: dark navy цвет, баланс
  // visible amount > currency (currency на blueSteel'е для иерархии).
  successAmountRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginTop: 24,
  },
  successAmount: {
    fontFamily: 'Inter',
    fontSize: 36,
    fontWeight: '700',
    lineHeight: 42,
    color: COLORS.textPrimary,
  },
  successCurrency: {
    fontFamily: 'Inter',
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.blueSteel,
  },
  successOkButton: {
    alignSelf: 'stretch',
    marginTop: 32,
    height: 48,
    borderRadius: 8,
    backgroundColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successOkButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
});

export default CloseDepositScreen;
